import { Box, Button, Divider, Event, Heading, TextBlock } from "@bcc-ui/ui-components";
import React from "react";
import { useTranslation } from "react-i18next";

import style from "./risk-resolving-step.scss";
import { RiskDistributionCard } from "components/asset-management-system/custom-views/tabs/risk/cards/risk-distribution-card/RiskDistributionCard";
import { SmartGradingBanner } from "components/asset-management-system/custom-views/tabs/value/steps/value-step-card/smart-grading-banner/SmartGradingBanner";
import { ValueStepCard } from "components/asset-management-system/custom-views/tabs/value/steps/value-step-card/ValueStepCard";
import { GradingStep, ResolveRiskSummary } from "services/asset-management/AssetGradingService";
import { RiskSummary } from "services/asset-management/AssetService";

import testIds from "testIds.json";

interface Props {
    isSmartGradingEnabled: boolean;
    handleSmartGradingChange: (value: boolean) => void;
    handleGradingStepSave: (step: GradingStep, enabled: boolean) => void;
    handleResolveRisks: () => void;
    handleContinueGrading: () => void;
    isLaunchingGrading: boolean;
    allAssetsEligibleForGrading: boolean;
    someAssetsEligibleForGrading: boolean;
    data: ResolveRiskSummary;
    currentlyCompletedStep: number;
}

const toRiskSummary = (data: ResolveRiskSummary): RiskSummary => ({
    totalLow: data.assetsExcludingLockedAndQuarantinedCount,
    totalMedium: data.quarantinedAssetsCounts,
    totalHigh: data.lockedAssetsCount,
    total: data.assetsExcludingLockedAndQuarantinedCount + data.quarantinedAssetsCounts + data.lockedAssetsCount,
});

export const RiskResolvingStep = (props: Props) => {
    const { t } = useTranslation();

    const eligibleAssetsCount = props.data.assetsExcludingLockedAndQuarantinedCount;
    const lockedAssetsCount = props.data.lockedAssetsCount;

    const riskDistributionCardContent = [
        {
            variant: "var(--app-low-risk-level-color)",
            heading: "AssetManagement.value.smartGrading.stepTwo.lowRiskTitle",
            description: "AssetManagement.value.smartGrading.stepTwo.lowRiskDescription",
            count: props.data?.assetsExcludingLockedAndQuarantinedCount,
        },
        {
            variant: "var(--app-medium-risk-level-color)",
            heading: "AssetManagement.value.smartGrading.stepTwo.mediumRiskTitle",
            description: "AssetManagement.value.smartGrading.stepTwo.mediumRiskDescription",
            count: props.data?.lockedAssetsCount,
        },
        {
            variant: "var(--app-high-risk-level-color)",
            heading: "AssetManagement.value.smartGrading.stepTwo.highRiskTitle",
            description: "AssetManagement.value.smartGrading.stepTwo.highRiskDescription",
            count: props.data?.quarantinedAssetsCounts,
        },
    ];

    const getActionDescription = () => {
        if (eligibleAssetsCount === 0) {
            return "";
        }
        return props.allAssetsEligibleForGrading
            ? t("AssetManagement.value.smartGrading.stepTwo.actionDescription_unlocked")
            : t("AssetManagement.value.smartGrading.stepTwo.actionDescription");
    };

    const riskStatusToGradingStatus = () =>
        riskDistributionCardContent.map((item, index) => (
            <Box
                key={index}
                gap={"Sm"}
                className={
                    props.allAssetsEligibleForGrading && index > 0 ? style.allAssetsEligibleForGrading : undefined
                }
            >
                <div className={style.riskVariantIcon} style={{ backgroundColor: item.variant }} />
                <Box columnLayout className={style.flexGrow}>
                    <Box columnLayout>
                        <Box>
                            <Heading variant={"SUBTITLE_2"} disableTopSpacing className={style.flexGrow}>
                                {t(item.heading)}
                            </Heading>
                            <Heading variant={"SUBTITLE_1"} disableBottomSpacing disableTopSpacing>
                                {item.count}
                            </Heading>
                        </Box>
                        <TextBlock className={style.descriptionColor}>{t(item.description)}</TextBlock>
                        {index < riskDistributionCardContent.length - 1 && (
                            <Divider direction={"Horizontal"} className={style.riskChartDivider} />
                        )}
                    </Box>
                </Box>
            </Box>
        ));

    return (
        <>
            <SmartGradingBanner
                currentStepNumber={props.currentlyCompletedStep}
                isSmartGradingEnabled={props.isSmartGradingEnabled}
                eligibleAssetsCount={eligibleAssetsCount}
                onSmartGradingChange={props.handleSmartGradingChange}
                onGradingStepSave={props.handleGradingStepSave}
            />

            <ValueStepCard
                currentStepNumber={2}
                heading={
                    eligibleAssetsCount === 0
                        ? t("AssetManagement.value.smartGrading.stepTwo.nothingToGradeYetTitle")
                        : props.allAssetsEligibleForGrading
                          ? t("AssetManagement.value.smartGrading.stepTwo.title_unlocked", {
                                count: eligibleAssetsCount,
                            })
                          : t("AssetManagement.value.smartGrading.stepTwo.title", {
                                totalLockedAssets: lockedAssetsCount + props.data.quarantinedAssetsCounts,
                            })
                }
                description={
                    eligibleAssetsCount === 0
                        ? t("AssetManagement.value.smartGrading.stepTwo.nothingToGradeYetDescription")
                        : props.allAssetsEligibleForGrading
                          ? t("AssetManagement.value.smartGrading.stepTwo.description_unlocked")
                          : t("AssetManagement.value.smartGrading.stepTwo.description", {
                                totalAssets: eligibleAssetsCount,
                                lockedAssets: lockedAssetsCount + props.data.quarantinedAssetsCounts,
                            })
                }
                leftSectionContent={
                    props.data && (
                        <RiskDistributionCard
                            data={toRiskSummary(props.data)}
                            hideHeading
                            horizontalDirection
                            lowRiskLabel={t("AssetManagement.value.smartGrading.stepTwo.lowRiskTitle")}
                            mediumRiskLabel={t("AssetManagement.value.smartGrading.stepTwo.mediumRiskTitle")}
                            highRiskLabel={t("AssetManagement.value.smartGrading.stepTwo.highRiskTitle")}
                            customContent={
                                <Box columnLayout horizontalAlignment={"Center"}>
                                    {riskStatusToGradingStatus()}
                                </Box>
                            }
                        />
                    )
                }
                rightSectionContent={
                    <Box columnLayout paddingTop={"Xs"}>
                        <Heading variant={"SUBTITLE_1"} disableBottomSpacing disableTopSpacing>
                            {getActionDescription()}
                        </Heading>
                        <Box paddingTop={"Lg"} gap={"Md"} flexWrap={"Wrap"}>
                            {!props.allAssetsEligibleForGrading && (
                                <Button
                                    testId={testIds.workArea.asset.customView.value.resolveRisksButton}
                                    variant={"Secondary"}
                                    icon={<Event />}
                                    disabled={lockedAssetsCount + props.data.quarantinedAssetsCounts <= 0}
                                    onClick={props.handleResolveRisks}
                                >
                                    {t("AssetManagement.value.smartGrading.stepTwo.resolveRisks")}
                                </Button>
                            )}
                            <Button
                                variant={"Secondary"}
                                testId={testIds.workArea.asset.customView.value.continueGradingButton}
                                disabled={
                                    eligibleAssetsCount === 0 ||
                                    props.isLaunchingGrading ||
                                    !props.someAssetsEligibleForGrading ||
                                    !props.isSmartGradingEnabled
                                }
                                onClick={props.handleContinueGrading}
                            >
                                {t("AssetManagement.value.smartGrading.stepTwo.continueGrading")}
                            </Button>
                        </Box>
                    </Box>
                }
            />
        </>
    );
};
